import React, { useEffect, useState } from 'react'
import { useNavigate, NavLink } from 'react-router-dom'
import {
  ActionMenu,
  BodyText,
  Card,
  Col,
  IconButton,
  LoadingSpinner,
  OverflowIcon,
  PAYPAL_THEME,
  Row,
  WarningIcon,
} from '@paypalcorp/pp-react'
import PropTypes from 'prop-types'
import { Message } from '@paypalcorp/worldready-react'
import { useApplicationContext } from '../../../context/ApplicationContext'
import moment from 'moment'
import { recordDynamicEvent } from '../../../components/common/analytics/utils'
import { saveBannerProgress } from '../../../utils/session-utils'
import { rangeLabel } from '../../../utils/date'
import constant from '../../../common/constant'
import { ExternalLink } from '../../../components/common'

const SummaryBadge = ({ type, closeBadge, linkFullPage, count = 0 }) => {
  const navigate = useNavigate()
  const Comp = type === 'error-log' ? NavLink : ExternalLink
  const summaryBadgeType =
    type === 'error-log'
      ? constant.FPTI_PAGES_CATEGORIES.HOME_PAGE_ERROR_SUMMARY
      : constant.FPTI_PAGES_CATEGORIES.HOME_PAGE_RECENT_TRANSACTION
  const location = window.location.href
  let host = constant.HOST_NAME.MSM
  if (
    location.indexOf('developer.paypal.com') > -1 ||
    location.indexOf('dnwsmoke.paypal.com') > -1
  )
    host = constant.HOST_NAME.LIVE

  const {
    dateRangeSelectorStart, // set by date-range-select
    dateRangeSelectorEnd, // set by date-range-select
    logger,
    csrf,
    homePageDataLoading,
  } = useApplicationContext()

  const { sysColorLinkMain } = PAYPAL_THEME
  const [dateRangeLabel, setDateRangeLabel] = useState('')
  const [suffix, setSuffix] = useState('')
  const [isOpen, setOpen] = useState(false)

  const options = [
    {
      title:
        type === 'error-log' ? (
          <Message id="summary-badge.error-log.detail.redirect" />
        ) : (
          <Message id="summary-badge.recent-transactions.detail.redirect" />
        ),
      action: 'redirect',
    },
    {
      title: <Message id="summary-badge.close" />,
      action: 'close_summary',
    },
  ]

  // Handle the Action Menu selections
  const handleSelection = (selectedIndex) => {
    switch (options[selectedIndex].action) {
      case 'redirect': {
        recordDynamicEvent(
          `${type}-summary,action-menu-click,redirect-full-page`,
        )
        if (type === 'error-log') {
          navigate(`${linkFullPage}`)
        } else if (type === 'recent-transactions') {
          setOpen(false)
          window.open(host + '/activities', '_blank')
        }
        break
      }
      case 'close_summary': {
        closeBadge(true)
        recordDynamicEvent(`${type}-summary,action-menu-click,dismiss`)

        let sessionObject
        if (type === 'error-log') {
          sessionObject = {
            closeErrorSummaryBanner: true,
          }
        } else if (type === 'recent-transactions') {
          sessionObject = {
            closeTransactionBanner: true,
          }
        }
        saveBannerProgress(csrf, logger, sessionObject)
        break
      }
      default:
        break
    }
  }

  useEffect(() => {
    if (homePageDataLoading) {
      return
    }

    // update labels based on start/end
    const obj = rangeLabel(
      moment(dateRangeSelectorStart),
      moment(dateRangeSelectorEnd),
    )
    setSuffix(obj.suffix)
    setDateRangeLabel(obj.label)
  }, [homePageDataLoading])

  const badgeSummary = (
    <Card className="recent-transactions_summary-container">
      {homePageDataLoading && (
        <LoadingSpinner
          screenReaderText={<Message id="summary-badge.loading" />}
          className="recent-transaction_summary-spinner"
        />
      )}

      {!homePageDataLoading && (
        <>
          <Row>
            <Col xs={10}>
              <WarningIcon size="sm" color="sysColorErrorMain" />
            </Col>
            <Col className="recent-transactions_summary-dismiss">
              <IconButton
                buttonType="tertiary"
                onClick={() => setOpen(!isOpen)}
                size="xs"
                svgIconComponent={OverflowIcon}
              />
              <ActionMenu
                className="actionMenuWidth"
                theme="v2"
                isOpen={isOpen}
                options={options}
                onSelection={handleSelection}
                onClose={() => setOpen(false)}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <BodyText strong style={{ color: sysColorLinkMain }}>
                <Comp
                  to={`${linkFullPage}`}
                  className="dev_dash__view_all_recent_transactions"
                  href={`${host}/activities`}
                  target={type === 'error-log' ? '_self' : '_blank'}
                  data-dxa={summaryBadgeType + ',click,' + type}
                >
                  {count === 1 ? (
                    <Message id={`summary-badge.${type}.header.count-one`} />
                  ) : (
                    <Message
                      id={`summary-badge.${type}.header.count`}
                      headerCount={count}
                    />
                  )}
                </Comp>
              </BodyText>
            </Col>
          </Row>
          <Row className="recent-transactions_summary-detail">
            <Col>
              <BodyText>
                <Message id={`summary-badge.${type}.detail.date`} />
                {` ${dateRangeLabel}`}
                {suffix === 'hour' && <Message id="summary-badge.label.hour" />}
                {suffix === 'hours' && (
                  <Message id="summary-badge.label.hours" />
                )}
                {suffix === 'days' && <Message id="summary-badge.label.days" />}
                {count === 1 ? (
                  <Message id={`summary-badge.${type}.detail.count-one`} />
                ) : (
                  <Message
                    id={`summary-badge.${type}.detail.count`}
                    detailCount={count}
                  />
                )}
              </BodyText>
            </Col>
          </Row>
        </>
      )}
    </Card>
  )

  return <>{count > 0 ? badgeSummary : null}</>
}

SummaryBadge.propTypes = {
  type: PropTypes.string.isRequired, // Supported: 'error-log' | 'recent-transactions' (for localized text phrases)
  closeBadge: PropTypes.func.isRequired, // function that handles the Dismiss link
  linkFullPage: PropTypes.string.isRequired, // target link href for full page view
  count: PropTypes.number.isRequired, // count of errors/fails
}

export default SummaryBadge
